/* eslint-disable no-param-reassign */
import FormToJSON from './form-to-json';
import ErrorHandling from './error-handling';
import Helpers from './helpers';
import Ping from './ping';

export default class AsyncForm {
	constructor({ elements }) {
		elements.forEach((el) => {
			el.addEventListener('submit', (e) => {
				e.preventDefault();
				Helpers.disableAllButtons();
				this.handleFormSubmit({ element: el });
			});
		});
	}

	handleFormSubmit({ element }) {
		this.element = element;
		Helpers.saveCodeMirrorInput();
		this.apiEndpoint = element.dataset?.asyncForm || element.dataset?.apiEndpoint;
		this.isBO = element.dataset?.isbo || false;
		this.async = element.dataset?.async !== 'false';
		this.validate = element.dataset?.validate || false;
		this.noResponseHandling = element.dataset?.noResponseHandling || false;
		this.token = element.dataset?.token;
		this.identify = element.dataset?.identify || false;
		this.antiForgeryToken = element.dataset?.antiForgeryToken || false;
		this.hasIdCheck = element.dataset?.addId?.toLowerCase() === 'true';
		this.errorPing = {
			heading: element.dataset?.pingSadHeading,
			body: element.dataset?.pingSadBody,
		};

		if (this.hasIdCheck) {
			Helpers.addHiddenIdField(this.element);
		}

		let response = false;

		if (this.validate && !this.validateForm()) {
			Helpers.enableAllButtons();
			return response;
		}

		if (this.identify) {
			window.dispatchEvent(
				new CustomEvent('identify', {
					detail: {
						properties: {
							email: this.element?.querySelector('.email-input')?.value,
						},
					},
				})
			);
		}

		this.formMethod = element.method;
		this.json = FormToJSON(element);
		this.clearForm = element.dataset?.clearForm || false;
		if (this.json && this.formMethod && this.apiEndpoint) {
			this.cleanUpErrors();
			response = this.sendRequest(element);
		} else {
			// try normal submit
			this.element.submit();
		}
		return response;
	}

	sendRequest() {
		const apiBase = '/umbraco/api/';
		const xhr = new XMLHttpRequest();
		xhr.open(this.formMethod, `${apiBase}${this.apiEndpoint}`, this.async);
		xhr.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
		xhr.setRequestHeader('Culture', document.documentElement.lang);
		xhr.setRequestHeader('RequestVerificationToken', this.antiForgeryToken);
		if (this.token !== null && this.token !== undefined) {
			xhr.withCredentials = true;
			xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
		}
		return this.async ? this.handleAsyncRequest(xhr) : this.handleSyncRequest(xhr);
	}

	handleAsyncRequest(xhr) {
		xhr.responseType = 'json';

		// send the collected data as JSON
		xhr.send(JSON.stringify(this.json));

		xhr.onerror = () => {
			Ping.createSadPing(this.errorPing?.heading, this.errorPing?.body);
			Helpers.enableAllButtons();
		};

		xhr.onloadend = () => {
			if (!this.noResponseHandling) {
				this.handleRequestResponse(xhr);
			}
			Helpers.enableAllButtons();
			return true;
		};
		window.dispatchEvent(new CustomEvent('lightbox-close'));
	}

	handleSyncRequest(xhr) {
		try {
			// send the collected data as JSON
			xhr.send(JSON.stringify(this.json));

			let returnValue = true;
			if (!this.noResponseHandling) {
				returnValue = this.handleRequestResponse(xhr);
			}

			Helpers.enableAllButtons();
			window.dispatchEvent(new CustomEvent('lightbox-close'));
			return returnValue;
		} catch {
			Ping.createSadPing();
			Helpers.enableAllButtons();
			window.dispatchEvent(new CustomEvent('lightbox-close'));
			return false;
		}
	}

	handleRequestResponse(xhr) {
		let pingHeading;
		let pingBody;
		let response;
		let returnValue = false;
		try {
			response = JSON.parse(xhr.response);
		} catch {
			response = xhr.response;
		}

		if (response != null && response.ping && !response.hasRedirect) {
			pingHeading = response.ping.pingHeading;
			pingBody = response.ping.pingBody;
		}

		if (xhr.status >= 200 && xhr.status < 300) {
			if (response != null && response?.redirectUrl) {
				window.location = response?.redirectUrl;
			} else {
				Ping.createHappyPing(pingHeading, pingBody);

				window.dispatchEvent(new CustomEvent('save-succesful'));

				if (this.clearForm) {
					this.element.reset();
				}
			}
			returnValue = true;
		}

		if (xhr.status === 400) {
			window.dispatchEvent(new CustomEvent('return-false'));
			Ping.createMehPing(pingHeading, pingBody);

			// show what is wrong with the form
			this.handleError();
		}

		if (xhr.status === 404) {
			// if api is not there try normal submit
			this.element.submit();
		}

		if (xhr.status === 403) {
			window.dispatchEvent(new CustomEvent('return-false'));
			Ping.createMehPing(pingHeading, pingBody);
		}

		// seems a 500 does not land in the onError - TODO: check this
		if (xhr.status === 500) {
			window.dispatchEvent(new CustomEvent('return-false'));
			if (pingHeading !== undefined && pingBody !== undefined) {
				Ping.createSadPing(pingHeading, pingBody);
			} else {
				Ping.createSadPing(this.errorPing?.heading, this.errorPing?.body);
			}
		}
		return returnValue;
	}

	handleError() {
		if (!this.errorMessages) return;
		const errorDiv = document.createElement('div');
		errorDiv.setAttribute('class', 'error__container');
		// eslint-disable-next-line no-restricted-syntax
		for (const key of Object.keys(this.errorMessages)) {
			for (let index = 0; index < this.errorMessages[key].length; index += 1) {
				const errorMessage = document.createElement('p');
				errorMessage.innerText = this.errorMessages[key][index];
				errorDiv.appendChild(errorMessage);
			}
		}
		this.element.insertBefore(errorDiv, this.element.firstChild);
	}

	cleanUpErrors() {
		const errorContainers = this.element.getElementsByClassName('error__container');
		if (errorContainers) {
			Array.from(errorContainers).forEach((errorContainer) => {
				errorContainer.remove();
			});
		}
	}

	validateForm() {
		let valid = true;
		const inputs = this.element?.querySelectorAll('input, textarea, select');
		// A loop that checks every input field in the current tab:
		valid = ErrorHandling(inputs);
		return valid; // return the valid status
	}
}
